import { makeAutoObservable } from "mobx";

import {
  apiPutMedicalReportsUuid,
  client,
  type ExtractedDiagnosis,
  type ExtractedMeasurement,
  type ExtractedMedication,
  type ExtractedSymptom,
} from "../../../api";
import { RequestError } from "../../../api/api-connector";
import { analytics } from "../../../app/analytics";
import { maybeReportError } from "../../../app/errors";
import {
  fetchSupportedMedications,
  type SupportedMedications,
} from "../../../catalogs/supported-medications";
import { createRowId } from "../../../utils/utils";
import { ReportWithExtraction } from "./extraction-status";

export type ExtractedEntity =
  | ExtractedDiagnosis
  | ExtractedMedication
  | ExtractedSymptom
  | ExtractedMeasurement;

export type EntityKind =
  | "diagnoses"
  | "medications"
  | "symptoms"
  | "medical_data";

/** All four kinds, in the order the review panel shows them as sections. */
export const ENTITY_KINDS: EntityKind[] = [
  "diagnoses",
  "medications",
  "medical_data",
  "symptoms",
];

/** Field names per kind. One table, because label/date/value lookups are done
 *  in several places and three copies of it drift. */
export const FIELDS: Record<
  EntityKind,
  { label: string; date: string; value: string }
> = {
  diagnoses: {
    label: "diagnosis_name",
    date: "diagnosed_at",
    value: "diagnosis_value",
  },
  medications: {
    label: "medication_name",
    date: "medication_started",
    value: "medication_dose",
  },
  symptoms: {
    label: "symptom_name",
    date: "symptom_datetime",
    value: "symptom_value",
  },
  medical_data: {
    label: "measurement_type",
    date: "measurement_datetime",
    value: "measurement_value",
  },
};

export interface ExtractionResult {
  diagnoses: ExtractedDiagnosis[];
  medications: ExtractedMedication[];
  symptoms: ExtractedSymptom[];
  medical_data: ExtractedMeasurement[];
  report_type: number;
  report_date: string;
  /** The date the document text itself states, when it gives one. Not the
   *  same thing as `report_date`, which only echoes what the uploader typed. */
  document_date: string | null;
  confidence_score: number;
  source_language: string;
}

export interface BBox {
  page: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export type ExtractionBBoxes = Record<EntityKind, (BBox | null)[]>;

export type ExtractionEntityConfidence = Record<EntityKind, number[]>;

/** An existing clinical record an extracted entity may repeat. `value` arrives
 *  already decoded, so it can be shown to the reviewer as-is. */
export interface ExtractionDuplicateRef {
  uuid: string;
  recorded_at?: string | null;
  value?: number | string | null;
  unit?: string | null;
}

/** Index-aligned with the entity lists, like `bboxes`. A null element means the
 *  entity is not a duplicate; a missing or empty list means the result carries
 *  no dedup information at all (stored before the check existed, or from the
 *  region-extract endpoint, which does not run it). */
export type ExtractionDuplicates = Partial<
  Record<EntityKind, (ExtractionDuplicateRef | null)[]>
>;

/** Where an entity came from. The backend stamps the model's own output
 *  "model"; the other two are the reviewer's doing. */
export type EntityOrigin = "model" | "region" | "manual";

export type ExtractionEntityOrigin = Record<EntityKind, EntityOrigin[]>;

/** Per entity: kept in the draft, left out of the ingest. */
export type ExtractionExcluded = Record<EntityKind, boolean[]>;

/** Exactly one id per entity: server-minted for the model's output,
 *  client-minted for one the reviewer adds. It pairs the model's output with
 *  the reviewer's, so it survives edits, deletes and reorders. */
export type ExtractionEntityIds = Record<EntityKind, string[]>;

export interface ExtractionStoredResult {
  report_uuid: string;
  extraction: ExtractionResult;
  extraction_status: string;
  extracted_at: string;
  bboxes?: ExtractionBBoxes;
  /** Per-entity extraction-accuracy score: 1.0 quote+value verified in the
   *  document, 0.5 quote found but value not in it, 0.0 quote not found.
   *  Missing on results stored before the field existed. */
  entity_confidence?: ExtractionEntityConfidence;
  /** Entities that may already be on the patient's record. */
  duplicates?: ExtractionDuplicates;
  /** Empty per kind until a draft has been saved, and absent altogether on
   *  results stored before the field existed; an entry it does not reach is
   *  the model's own. */
  entity_origin?: ExtractionEntityOrigin;
  /** The reviewer's include decisions. Empty per kind until a draft has been
   *  saved, where the flags stand in for them. */
  excluded?: ExtractionExcluded;
  entity_ids: ExtractionEntityIds;
  /** The reviewer's note on the document as a whole. */
  review_comment?: string | null;
  /** Set once a reviewer has saved corrections over the model's output —
   *  so reopening a part-reviewed document resumes rather than restarts. */
  reviewed_at?: string | null;
  /** `rejected`: the reviewer judged the output unusable. */
  review_status:
    | "unreviewed"
    | "in_review"
    | "approved"
    | "rejected"
    | "superseded";
  is_current: boolean;
  /** Bumped on every draft write; a PUT echoing a stale one gets a 409. */
  version: number;
  /** entity_id -> ingested row uuid, written by the server on ingest. */
  ingested_uuids: Record<string, string>;
  /** entity_id -> reason, written by the server on ingest. */
  skip_reasons: Record<string, string>;
}

/** Response of the interactive region-extract endpoint. */
export interface RegionExtractResult {
  extraction: ExtractionResult;
  bboxes?: ExtractionBBoxes;
  entity_confidence?: ExtractionEntityConfidence;
  /** Always empty from this endpoint — candidates are checked against the
   *  review list rather than the patient's record (see `isDuplicate`). */
  duplicates?: ExtractionDuplicates;
  /** True when the selection was larger than the excerpt cap and only its
   *  first part was read. */
  text_truncated?: boolean;
  /** What the backend actually read under the selection — shown to the
   *  reviewer, since "nothing found" and "nothing readable" differ. */
  region_text: string;
  /** True when the text came from OCR rather than a PDF text layer. */
  ocr_used: boolean;
}

/** The region a reviewer selected, in PDF-point coordinates. `text` is set
 *  for digital PDFs, where the browser already knows what was highlighted. */
export interface RegionSelection {
  page: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  text?: string;
}

/** A region-extract candidate awaiting the reviewer's approval. Kept out of
 *  `entities` until approved so a bad suggestion never reaches ingest. */
export interface Suggestion {
  kind: EntityKind;
  entity: ExtractedEntity;
  bbox: BBox | null;
  /** Same deterministic grounding tier the document-wide entities carry: the
   *  backend locates each candidate's quote inside the selection. */
  confidence: number | null;
}

export class ExtractionModel {
  result: ExtractionStoredResult | null = null;
  /** `extraction_status` from the report row the reviewer opened — see
   *  `isIngested` for why the stored result is not enough on its own. */
  reportStatus: string | null = null;
  loading = false;
  ingesting = false;
  rejecting = false;
  applyingDate = false;
  /** The failure itself, not its text: `errorText` is pure, so resolving it at
   *  render keeps the reason in the reviewer's own language. */
  error: unknown = null;
  savingDraft = false;
  /** When the reviewed state was last accepted by the server, ISO 8601. */
  draftSavedAt: string | null = null;
  draftError: unknown = null;
  /** Signature of the last state the server accepted, so an edit that is
   *  undone back to the saved version does not count as unsaved. */
  private savedSignature = "";
  private version = 0;

  // --- interactive region extraction -------------------------------------
  suggestions: Suggestion[] = [];
  regionLoading = false;
  regionError: unknown = null;
  regionText = "";
  regionOcrUsed = false;
  regionTruncated = false;
  /** The last selection, so a failed call can be retried without re-dragging. */
  lastRegion: RegionSelection | null = null;
  /** When the in-flight call started, as a monotonic `performance.now()`
   *  reading — this measures elapsed time, not a wall-clock instant. The model
   *  behind this varies from ~9s to over the timeout on the same selection, so
   *  a silent spinner leaves the reviewer unable to tell a slow answer from a
   *  hung one. */
  regionStartedAt: number | null = null;
  private regionAbort: AbortController | null = null;
  // Monotonic load token: one shared model serves all reports, so a slow
  // response for report A must not overwrite a later load of report B
  // (latest-wins; stale responses are dropped).
  private loadSeq = 0;
  // Tail of the draft-write queue (see `saveDraft`).
  private draftWrite: Promise<void> = Promise.resolve();
  private medications: SupportedMedications | null = null;

  // Entities kept in kind-keyed maps, index-aligned with the stored result.
  entities: Record<EntityKind, ExtractedEntity[]> = {
    diagnoses: [],
    medications: [],
    symptoms: [],
    medical_data: [],
  };
  deleted: Record<EntityKind, Set<number>> = {
    diagnoses: new Set(),
    medications: new Set(),
    symptoms: new Set(),
    medical_data: new Set(),
  };
  // Entities flagged as possible duplicates — of a record the patient already
  // has, or of another mention of the same finding in this document — and not
  // (yet) opted back in. Kept apart from `deleted` deliberately: a removed
  // entity is pruned from the saved draft, so seeding these as removals would
  // destroy the flagged entities on the next autosave, before the reviewer had
  // decided anything. An excluded entity stays in the draft and in the list —
  // it is only left out of what gets written to the patient. Membership alone
  // is not exclusion: `isExcluded` also asks for a live flag, so an entity
  // whose flag has since gone (its primary removed, its value edited) counts
  // again without the reviewer touching it.
  excludedDuplicates: Record<EntityKind, Set<number>> = {
    diagnoses: new Set(),
    medications: new Set(),
    symptoms: new Set(),
    medical_data: new Set(),
  };
  // Boxes for entities added after load (region-extracted, or hand-drawn):
  // `result.bboxes` is index-aligned with the *extracted* lists only, so
  // anything appended needs its box kept alongside to stay highlightable.
  addedBboxes: Record<EntityKind, Record<number, BBox>> = {
    diagnoses: {},
    medications: {},
    symptoms: {},
    medical_data: {},
  };
  // Grounding tier for appended entities, same reason as addedBboxes: the
  // stored result's lists only line up with the extracted entities.
  addedConfidence: Record<EntityKind, Record<number, number>> = {
    diagnoses: {},
    medications: {},
    symptoms: {},
    medical_data: {},
  };
  // Where each entity came from, index-aligned with `entities`. Approved
  // extractions are the evaluation dataset, so which rows the model produced
  // and which the reviewer did has to survive a reload.
  origins: ExtractionEntityOrigin = {
    diagnoses: [],
    medications: [],
    symptoms: [],
    medical_data: [],
  };
  // index-aligned with entities, like origins
  entityIds: ExtractionEntityIds = {
    diagnoses: [],
    medications: [],
    symptoms: [],
    medical_data: [],
  };
  /** The reviewer's note on the document as a whole. */
  reviewComment: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  bbox(kind: EntityKind, idx: number): BBox | null {
    return (
      this.result?.bboxes?.[kind]?.[idx] ?? this.addedBboxes[kind][idx] ?? null
    );
  }

  /** The value a repeated entity is grouped by in the review list. The same
   *  measurement type at several dates (a report summarising years of blood
   *  pressures) collapses to one row instead of a dozen. */
  entityLabel(kind: EntityKind, idx: number): string {
    const e = this.entities[kind][idx] as unknown as
      | Record<string, unknown>
      | undefined;
    if (!e) return "";
    return String(e[FIELDS[kind].label] ?? "");
  }

  /** The entity's own date, used to order a group and to resolve it down to
   *  its most recent member. Empty when the entity carries no usable date. */
  entityDate(kind: EntityKind, idx: number): string {
    const e = this.entities[kind][idx] as unknown as
      | Record<string, unknown>
      | undefined;
    if (!e) return "";
    return String(e[FIELDS[kind].date] ?? "");
  }

  /** Extraction-accuracy score; null when unknown (a hand-added record, or an
   *  older stored result). */
  entityConfidence(kind: EntityKind, idx: number): number | null {
    return (
      this.result?.entity_confidence?.[kind]?.[idx] ??
      this.addedConfidence[kind][idx] ??
      null
    );
  }

  /** The record this entity may repeat, or null. Entities appended after the
   *  extraction (hand-added, or approved from a region) are never flagged:
   *  until the draft is saved their index is past the end of the list, and the
   *  saved draft holds an explicit null for them. */
  duplicateRef(kind: EntityKind, idx: number): ExtractionDuplicateRef | null {
    return this.result?.duplicates?.[kind]?.[idx] ?? null;
  }

  entityId(kind: EntityKind, idx: number): string {
    return this.entityIds[kind][idx];
  }

  /** Why the ingest skipped this entity, if it did. */
  skipReason(kind: EntityKind, idx: number): string | null {
    return this.result?.skip_reasons?.[this.entityId(kind, idx)] ?? null;
  }

  isDeleted(kind: EntityKind, idx: number): boolean {
    return this.deleted[kind].has(idx);
  }

  /** A removed record the reviewer typed leaves the list for good; a removed
   *  extracted or region-approved one stays, struck through and restorable. */
  isListed(kind: EntityKind, idx: number): boolean {
    return !this.isDeleted(kind, idx) || !this.isTyped(kind, idx);
  }

  isExcluded(kind: EntityKind, idx: number): boolean {
    return (
      this.excludedDuplicates[kind].has(idx) &&
      (this.duplicateRef(kind, idx) !== null ||
        this.repeatOf(kind, idx) !== null)
    );
  }

  toggleDelete(kind: EntityKind, idx: number) {
    if (this.deleted[kind].has(idx)) {
      this.deleted[kind].delete(idx);
      // Pairs with the removal it undoes, so a misclick is not read as a model
      // error.
      analytics.track(
        "Extraction Entity Restored",
        this.entityProps(kind, idx),
      );
      return;
    }
    this.deleted[kind].add(idx);
    analytics.track("Extraction Entity Removed", this.entityProps(kind, idx));
  }

  toggleExcluded(kind: EntityKind, idx: number) {
    if (this.isExcluded(kind, idx)) {
      this.excludedDuplicates[kind].delete(idx);
    } else {
      this.excludedDuplicates[kind].add(idx);
    }
  }

  /** Apply the reviewer's corrections. Fields absent from the patch keep their
   *  current value, so the document quote and grounding score survive it. */
  setFields(kind: EntityKind, idx: number, patch: Record<string, unknown>) {
    const list = this.entities[kind] as unknown as Record<string, unknown>[];
    if (!list[idx]) return;
    // Which fields, never their values: a corrected dose is the patient's.
    // Compared as `signatureOf` does: the form rebuilds every field, so the
    // dosage comes back a fresh array and an absent field comes back null.
    const fields = Object.keys(patch).filter(
      (field) =>
        JSON.stringify(patch[field] ?? null) !==
        JSON.stringify(list[idx][field] ?? null),
    );
    list[idx] = { ...list[idx], ...patch };
    this.entities[kind] = [...list] as unknown as ExtractedEntity[];
    if (fields.length > 0) {
      analytics.track("Extraction Entity Edited", {
        ...this.entityProps(kind, idx),
        fields,
      });
    }
  }

  setReviewComment(comment: string | null) {
    this.reviewComment = comment;
  }

  // Added by hand or from a region extract. With `bbox` set it stays
  // highlightable in the PDF; either way it rides along in the saved draft.
  addEntity(
    kind: EntityKind,
    entity: ExtractedEntity,
    bbox: BBox | null = null,
    confidence: number | null = null,
    origin: EntityOrigin = "manual",
  ) {
    const idx = this.entities[kind].length;
    this.entities[kind] = [...this.entities[kind], entity];
    this.origins[kind] = [...this.origins[kind], origin];
    this.entityIds[kind] = [...this.entityIds[kind], createRowId()];
    if (bbox) this.addedBboxes[kind][idx] = bbox;
    if (confidence !== null) this.addedConfidence[kind][idx] = confidence;
  }

  /** Kind, label and value only: the model rarely repeats the other fields
   *  or the date identically. A medication's value is {@link medicationDose},
   *  so a combination drug's dose never splits one drug in two. */
  private identity(kind: EntityKind, entity: ExtractedEntity): string {
    const e = entity as unknown as Record<string, unknown>;
    const { label, value } = FIELDS[kind];
    const shown =
      kind === "medications"
        ? this.medicationDose(entity as ExtractedMedication).medication_dose
        : e[value];
    return [
      String(e[label] ?? "")
        .trim()
        .toLowerCase(),
      String(shown ?? "")
        .trim()
        .toLowerCase(),
    ].join("|");
  }

  medicationDose(
    m: ExtractedMedication,
  ): Pick<ExtractedMedication, "medication_dose" | "medication_unit"> {
    return (
      this.medications?.combinationDose(
        m.medication_group,
        m.medication_name,
      ) ?? m
    );
  }

  /** Index of an existing, not-removed record equivalent to `entity`. */
  private existingIndex(
    kind: EntityKind,
    entity: ExtractedEntity,
  ): number | undefined {
    const id = this.identity(kind, entity);
    const idx = this.entities[kind].findIndex(
      (e, i) => !this.deleted[kind].has(i) && this.identity(kind, e) === id,
    );
    return idx === -1 ? undefined : idx;
  }

  /**
   * Per kind, the mention each index belongs to: the best-grounded live mention
   * carrying its identity, or null when its identity has no live mention left.
   * Best-grounded means the highest confidence score (an unknown one ranks
   * below every score), then a located mention over an unlocated one, then the
   * lowest index.
   *
   * Derived rather than stored — removing a row or editing its value changes
   * which mentions repeat which — but derived once for the whole list rather
   * than per lookup. `repeatOf` is asked on every row render, and `flatRows`
   * asks it across the document on every keypress; the identity scan that
   * answered one call now answers all of them.
   */
  private get primaryByIndex(): Record<EntityKind, (number | null)[]> {
    return Object.fromEntries(
      ENTITY_KINDS.map((kind) => {
        const live = new Map<string, number[]>();
        this.entities[kind].forEach((e, i) => {
          if (this.deleted[kind].has(i)) return;
          const id = this.identity(kind, e);
          const members = live.get(id);
          if (members) members.push(i);
          else live.set(id, [i]);
        });
        const primary = new Map<string, number>();
        live.forEach((members, id) =>
          primary.set(
            id,
            members.reduce((best, i) =>
              this.outranks(kind, i, best) ? i : best,
            ),
          ),
        );
        return [
          kind,
          this.entities[kind].map(
            (e) => primary.get(this.identity(kind, e)) ?? null,
          ),
        ];
      }),
    ) as Record<EntityKind, (number | null)[]>;
  }

  /**
   * The mention this entity repeats within the same document, or null when this
   * entity is that mention or has no live twin.
   *
   * A removed mention still belongs to its finding, so it too reports the live
   * primary — that is what keeps its row folded under the finding it was
   * removed from rather than surfacing on its own.
   */
  repeatOf(kind: EntityKind, idx: number): number | null {
    const primary = this.primaryByIndex[kind][idx] ?? null;
    return primary === idx ? null : primary;
  }

  mentionsOf(kind: EntityKind, idx: number): number[] {
    return this.primaryByIndex[kind].flatMap((primary, i) =>
      primary === idx && i !== idx && this.isListed(kind, i) ? [i] : [],
    );
  }

  /** True when mention `a` is better grounded than mention `b`. */
  private outranks(kind: EntityKind, a: number, b: number): boolean {
    const ca = this.entityConfidence(kind, a) ?? -1;
    const cb = this.entityConfidence(kind, b) ?? -1;
    if (ca !== cb) return ca > cb;
    const ba = this.bbox(kind, a) !== null;
    if (ba !== (this.bbox(kind, b) !== null)) return ba;
    return a < b;
  }

  /** Re-run the last selection — the interactive path fails transiently (a slow
   *  model, a busy OCR worker), and re-dragging the same box is needless work. */
  async retryRegion(reportUuid: string): Promise<void> {
    if (this.lastRegion) await this.extractRegion(reportUuid, this.lastRegion);
  }

  async extractRegion(
    reportUuid: string,
    region: RegionSelection,
  ): Promise<void> {
    this.regionLoading = true;
    this.regionError = null;
    this.suggestions = [];
    this.regionText = "";
    this.regionTruncated = false;
    this.lastRegion = region;
    this.regionStartedAt = performance.now();
    analytics.track("Extraction Region Used", { report_id: reportUuid });
    this.regionAbort?.abort();
    const abort = new AbortController();
    this.regionAbort = abort;
    try {
      const resp = await client.post<RegionExtractResult>({
        url: `/api/v1/medical-reports/${reportUuid}/extraction/extract-region`,
        body: region,
        signal: abort.signal,
      });
      const data = resp.data;
      if (!data) return;
      this.regionText = data.region_text;
      this.regionOcrUsed = data.ocr_used;
      this.regionTruncated = !!data.text_truncated;
      this.suggestions = ENTITY_KINDS.flatMap((kind) =>
        data.extraction[kind].map((entity, i) => ({
          kind,
          entity,
          bbox: data.bboxes?.[kind]?.[i] ?? null,
          confidence: data.entity_confidence?.[kind]?.[i] ?? null,
        })),
      );
    } catch (e: unknown) {
      // A cancel is the reviewer's own doing, not a failure to report.
      if (abort.signal.aborted) return;
      // Shown inline instead of reaching the global dialog, so report it here
      // or a genuine bug in this path goes unnoticed.
      maybeReportError(e);
      this.regionError = e;
    } finally {
      // Only the call that still owns the slot may clear the progress state:
      // a superseded call settles *after* its replacement has started, and
      // clearing then would hide the live call's spinner, elapsed time and
      // cancel button (latest-wins, as in `load`).
      if (this.regionAbort === abort) {
        this.regionAbort = null;
        this.regionLoading = false;
        this.regionStartedAt = null;
      }
    }
  }

  /** Attach (or replace) a record's location in the document. Used when a
   *  reviewer marks where a hand-entered record came from — optional, since a
   *  manual record is valid without one. */
  setBbox(kind: EntityKind, idx: number, bbox: BBox) {
    this.addedBboxes[kind] = { ...this.addedBboxes[kind], [idx]: bbox };
  }

  /** Abandon the in-flight call. The reviewer keeps working instead of watching
   *  a spinner they cannot influence; the selection is kept for a retry. */
  cancelRegion() {
    this.regionAbort?.abort();
    this.regionAbort = null;
    this.regionLoading = false;
    this.regionStartedAt = null;
    this.suggestions = [];
  }

  approveSuggestion(i: number) {
    const s = this.suggestions[i];
    if (!s) return;
    this.addEntity(s.kind, s.entity, s.bbox, s.confidence, "region");
    this.suggestions = this.suggestions.filter((_, j) => j !== i);
  }

  /** True when an equivalent record is already in the review list — selecting
   *  an area twice, or an area the document-wide pass already covered, is
   *  common, and silently adding a second copy would reach ingest. Derived on
   *  read rather than stored with the candidate: the list it compares against
   *  grows as candidates are approved, and two identical candidates in one
   *  selection are duplicates of each other. */
  isDuplicate(s: Suggestion): boolean {
    return this.existingIndex(s.kind, s.entity) !== undefined;
  }

  /** Candidates that are not already in the list — what "Add all" would add. */
  get addableSuggestionCount(): number {
    return this.suggestions.filter((s) => !this.isDuplicate(s)).length;
  }

  approveAllSuggestions() {
    // Re-checked per iteration, so approving a candidate also skips its twin
    // later in the same batch.
    for (const s of this.suggestions) {
      if (!this.isDuplicate(s)) {
        this.addEntity(s.kind, s.entity, s.bbox, s.confidence, "region");
      }
    }
    this.suggestions = [];
  }

  dismissSuggestion(i: number) {
    this.suggestions = this.suggestions.filter((_, j) => j !== i);
  }

  clearSuggestions() {
    this.suggestions = [];
    this.regionError = null;
    this.regionText = "";
    this.regionTruncated = false;
    this.lastRegion = null;
    this.regionAbort?.abort();
    this.regionAbort = null;
    // Cleared here rather than left to the aborted call's `finally`: that only
    // runs when the call still owns the slot, and it no longer does. Without
    // this, closing the panel mid-extraction leaves the next document showing a
    // spinner for a call nobody is waiting on.
    this.regionLoading = false;
    this.regionStartedAt = null;
  }

  /** Mark every member of a group deleted — one action for a whole repeated
   *  block (e.g. a decade of blood pressures nobody wants ingested). */
  deleteIndices(kind: EntityKind, indices: number[]) {
    for (const i of indices) {
      // Already-removed members are re-added to the set on every group action,
      // and each would otherwise report a removal that did not happen.
      if (this.deleted[kind].has(i)) continue;
      this.deleted[kind].add(i);
      analytics.track("Extraction Entity Removed", this.entityProps(kind, i));
    }
    this.deleted[kind] = new Set(this.deleted[kind]);
  }

  /** Drop every member of a group dated before its latest day, keeping all the
   *  readings taken on that day. A no-op when there is no latest day. Members
   *  are matched on their own day rather than on what survived, so a mention
   *  folded under a dropped reading goes with it instead of taking its place. */
  keepLatestOnly(kind: EntityKind, indices: number[]) {
    const day = this.latestDay(kind, indices);
    if (day === null) return;
    this.deleteIndices(
      kind,
      indices.filter((i) => this.entityDay(kind, i) !== day),
    );
  }

  /**
   * The most recent day a group's live readings were taken, or null when it
   * carries at most one day.
   *
   * Also null when any live reading is undated: nothing places it against the
   * others, and dropping it as the older one would be a guess.
   */
  latestDay(kind: EntityKind, indices: number[]): string | null {
    const days = this.liveReadings(kind, indices).map((i) =>
      this.entityDay(kind, i),
    );
    if (days.some((day) => day === "")) return null;
    const distinct = [...new Set(days)].sort();
    return distinct.length > 1 ? (distinct.at(-1) ?? null) : null;
  }

  /**
   * True when the day that survives `keepLatestOnly` — the latest day, or the
   * group's only day where there is no latest — carries two or more live
   * readings. That is the group no action resolves to a single row: the
   * document states several values for the finding on one day, and which of
   * them to keep is the reviewer's to decide.
   *
   * False as soon as a live reading is undated. `keepLatestOnly` is not offered
   * for such a group, so no day survives it to be judged.
   */
  sameDayGroup(kind: EntityKind, indices: number[]): boolean {
    const days = this.liveReadings(kind, indices).map((i) =>
      this.entityDay(kind, i),
    );
    if (days.length < 2 || days.some((day) => day === "")) return false;
    const surviving = this.latestDay(kind, indices) ?? days[0];
    return days.filter((day) => day === surviving).length > 1;
  }

  /** The members of a group that count as readings of their own: neither
   *  removed, nor folded under another mention of the same finding. */
  private liveReadings(kind: EntityKind, indices: number[]): number[] {
    return indices.filter(
      (i) => !this.isDeleted(kind, i) && this.repeatOf(kind, i) === null,
    );
  }

  /** The date `entityDate` reports, without any time; empty when it reports
   *  none. */
  private entityDay(kind: EntityKind, idx: number): string {
    return this.entityDate(kind, idx).slice(0, 10);
  }

  /** True for records the model did not produce — typed by hand, or approved
   *  from a region. */
  isManual(kind: EntityKind, idx: number): boolean {
    return this.origins[kind][idx] !== "model";
  }

  /** True for records typed by hand, not approved from a region. */
  isTyped(kind: EntityKind, idx: number): boolean {
    return this.origins[kind][idx] === "manual";
  }

  /** True when no record in the list is the model's. A saved draft that pruned
   *  every model record reads the same way once reopened. */
  get modelFoundNothing(): boolean {
    return ENTITY_KINDS.every((kind) => !this.origins[kind].includes("model"));
  }

  /** Indices that will actually reach the patient's record: everything the
   *  reviewer neither removed nor left excluded under a flag — a chart
   *  duplicate or a repeat within the document. The conditions overlap
   *  freely, so this is the union rather than separate counts. */
  keptIndices(kind: EntityKind): number[] {
    return this.entities[kind].flatMap((_, i) =>
      this.isDeleted(kind, i) || this.isExcluded(kind, i) ? [] : [i],
    );
  }

  get totalKept(): number {
    return ENTITY_KINDS.reduce((sum, k) => sum + this.keptIndices(k).length, 0);
  }

  // --- draft persistence -------------------------------------------------
  // Review is not one sitting: correcting a dense letter takes minutes, and
  // the queue exists so documents are worked through in batches. Holding that
  // only here meant a reload or a misclick threw it away, so the reviewed
  // state is written back to the server as the reviewer works.

  /**
   * The reviewed state as the server should hold it: surviving entities with
   * their boxes and confidence tiers, still index-aligned after removals.
   */
  private draftPayload(): DraftPayload | null {
    if (!this.result) return null;
    const extraction = { ...this.result.extraction };
    const bboxes = {} as ExtractionBBoxes;
    const confidence = {} as ExtractionEntityConfidence;
    const duplicates: ExtractionDuplicates = {};
    const origin = {} as ExtractionEntityOrigin;
    const excluded = {} as ExtractionExcluded;
    const ids = {} as ExtractionEntityIds;
    for (const kind of ENTITY_KINDS) {
      const keep = this.entities[kind]
        .map((entity, idx) => ({ entity, idx }))
        .filter(({ idx }) => !this.deleted[kind].has(idx));
      extraction[kind] = keep.map(({ entity }) => entity) as never;
      bboxes[kind] = keep.map(({ idx }) => this.bbox(kind, idx));
      // The server requires a tier per entity, so an unscored hand-added
      // record reports 0 rather than shifting every later row by one.
      confidence[kind] = keep.map(
        ({ idx }) => this.entityConfidence(kind, idx) ?? 0,
      );
      // Pruned in lockstep with the entities, for the same reason: the server
      // rejects a list that is neither empty nor one row per entity, and a
      // draft saved without these silently drops the flags.
      duplicates[kind] = keep.map(({ idx }) => this.duplicateRef(kind, idx));
      origin[kind] = keep.map(({ idx }) => this.origins[kind][idx] ?? "model");
      // The include decision, which the ingest now reads from here rather than
      // being told: a re-inclusion that did not travel with the draft would be
      // undone by the next load.
      excluded[kind] = keep.map(({ idx }) => this.isExcluded(kind, idx));
      ids[kind] = keep.map(({ idx }) => this.entityId(kind, idx));
    }
    return {
      extraction,
      bboxes,
      entity_confidence: confidence,
      duplicates,
      entity_origin: origin,
      excluded,
      entity_ids: ids,
      review_comment: this.reviewComment,
      version: this.version,
    };
  }

  /**
   * Signature of the reviewed state, for spotting edits worth saving.
   *
   * Covers the whole payload, not just `extraction`: marking where a record
   * came from changes only `bboxes`, so a signature over the entities alone
   * left `hasUnsavedEdits` false — the autosave never fired, closing the panel
   * skipped its save, and the location the reviewer had just marked was gone
   * on reload. Same for a confidence tier arriving with an approved candidate.
   *
   * `version` and `entity_ids` are excluded — see `signatureOf`.
   */
  get draftSignature(): string {
    const payload = this.draftPayload();
    return payload ? signatureOf(payload) : "";
  }

  /** True while the reviewer has corrections the server has not accepted, or
   *  the last save failed: undoing the edit that broke it matches the saved
   *  signature again, and only a save that succeeds clears the error. */
  get hasUnsavedEdits(): boolean {
    return (
      this.result != null &&
      !this.isIngested &&
      (this.draftError != null || this.draftSignature !== this.savedSignature)
    );
  }

  /** The last save got a 409: the draft changed elsewhere, and only a reload
   *  recovers. */
  get draftConflict(): boolean {
    return (
      this.draftError instanceof RequestError &&
      this.draftError.response.status === 409
    );
  }

  /**
   * Persist the reviewed state. Safe to call repeatedly — the endpoint
   * overwrites, so a failed save is simply retried by the next one.
   *
   * `force` writes even when nothing has changed since the load. The ingest
   * reads the draft the server holds, and on a document nobody has edited that
   * draft has never been told which entities the duplicate and repeat flags
   * left out — the flags are read here, and the repeats are only derivable
   * here.
   *
   * Writes queue behind one another: two of them in flight can reach the
   * server in either order, so a debounced autosave could land after the save
   * an ingest waits for and leave the ingest reading the older draft.
   */
  async saveDraft(reportUuid: string, force = false): Promise<void> {
    this.draftWrite = this.draftWrite.then(() =>
      this.writeDraft(reportUuid, force),
    );
    return this.draftWrite;
  }

  private async writeDraft(reportUuid: string, force: boolean): Promise<void> {
    const payload = this.draftPayload();
    if (!payload || this.isIngested) return;
    if (!force && !this.hasUnsavedEdits) return;
    // Of the payload actually being sent, so what the server accepted and what
    // `savedSignature` records can never disagree.
    const signature = signatureOf(payload);
    this.savingDraft = true;
    this.draftError = null;
    try {
      const resp = await client.put<ExtractionStoredResult>({
        url: `/api/v1/medical-reports/${reportUuid}/extraction`,
        body: payload,
      });
      this.savedSignature = signature;
      this.version = resp.data?.version ?? this.version;
      // The server's stamp, not this browser's clock: it is what a colleague
      // reopening the document will see.
      this.draftSavedAt = resp.data?.reviewed_at ?? null;
    } catch (e: unknown) {
      // Kept on screen rather than thrown: the reviewer needs to know their
      // corrections are only in the browser, but interrupting the review with
      // a dialog on every transient failure would be worse than a warning.
      // Reported all the same, since the dialog is what would have done it.
      maybeReportError(e);
      this.draftError = e;
    } finally {
      this.savingDraft = false;
    }
  }

  /**
   * Already ingested — the panel is view-only (no editing/removing/ingest).
   *
   * Two sources say so, and either is enough. The report row is the one the
   * reports table and the queue trust, and after an ingest db-api updates it
   * while leaving the stored extraction record at "ready" — so relying on the
   * stored record alone offered "Approve & Ingest" for documents already
   * written to the patient, which would duplicate their data. Erring towards
   * read-only is the safe direction: the cost is a document that cannot be
   * re-ingested, not one ingested twice.
   */
  get isIngested(): boolean {
    return (
      this.result?.extraction_status === "ingested" ||
      this.reportStatus === "ingested"
    );
  }

  /**
   * What an entity-level event may carry. The extracted text is ePHI and stays
   * out of Mixpanel — the box locates it in the document instead, so a value
   * can be read back from the report itself when one needs looking at.
   */
  private entityProps(kind: EntityKind, idx: number) {
    const bbox = this.bbox(kind, idx);
    return {
      report_id: this.result?.report_uuid ?? null,
      entity_kind: kind,
      entity_confidence: this.entityConfidence(kind, idx),
      manual: this.isManual(kind, idx),
      page: bbox?.page ?? null,
      x0: bbox?.x0 ?? null,
      y0: bbox?.y0 ?? null,
      x1: bbox?.x1 ?? null,
      y1: bbox?.y1 ?? null,
    };
  }

  private reset() {
    this.result = null;
    this.reportStatus = null;
    this.error = null;
    this.savedSignature = "";
    this.draftSavedAt = null;
    this.draftError = null;
    this.savingDraft = false;
    this.reviewComment = null;
    this.version = 0;
    this.clearSuggestions();
    for (const k of ENTITY_KINDS) {
      this.entities[k] = [];
      this.deleted[k] = new Set();
      this.excludedDuplicates[k] = new Set();
      this.addedBboxes[k] = {};
      this.addedConfidence[k] = {};
      this.origins[k] = [];
      this.entityIds[k] = [];
    }
  }

  async load(report: ReportWithExtraction) {
    const seq = ++this.loadSeq;
    this.reset();
    this.reportStatus = report.extraction_status ?? null;
    this.loading = true;
    try {
      // The catalog decides which medications repeat one another, so it has
      // to be here before the repeats are seeded below.
      const [resp, medications] = await Promise.all([
        client.get<ExtractionStoredResult>({
          url: `/api/v1/medical-reports/${report.uuid}/extraction`,
        }),
        fetchSupportedMedications(),
      ]);
      if (seq !== this.loadSeq) return; // stale response — a newer load won
      this.medications = medications;
      const data = resp.data;
      if (data) {
        this.result = data;
        for (const k of ENTITY_KINDS) {
          this.entities[k] = [...data.extraction[k]];
          this.origins[k] = this.entities[k].map(
            (_, i) => data.entity_origin?.[k]?.[i] ?? "model",
          );
          // Per the contract the server always sends one id per entity; the
          // fallback only guards an unexpected gap.
          this.entityIds[k] = this.entities[k].map(
            (_, i) => data.entity_ids?.[k]?.[i] ?? createRowId(),
          );
        }
        this.reviewComment = data.review_comment ?? null;
        // A flagged entity starts out of the ingest, so the default action on
        // a document full of restated history is the safe one. Both flags seed
        // it: a record the patient may already have, and a finding this
        // document states more than once. Seeding is for a document nobody has
        // reviewed yet — once a draft carries the decisions, they are the
        // reviewer's and re-deriving them would undo every re-inclusion. The
        // server always sends `excluded`, so its presence says nothing: a list
        // with one entry per entity is a saved draft, and an empty one is how
        // it says nothing was decided, as with every other side-car list.
        // Skipped for an ingested document, which is read-only and has nothing
        // to exclude from.
        //
        // Load-only on purpose. A twin appended later (a candidate approved
        // from a region) carries the flag but is not excluded — the reviewer
        // just asked for it.
        if (!this.isIngested) {
          for (const k of ENTITY_KINDS) {
            const saved = data.excluded?.[k];
            if (
              saved !== undefined &&
              saved.length === this.entities[k].length &&
              saved.length > 0
            ) {
              saved.forEach((out, i) => {
                if (out) this.excludedDuplicates[k].add(i);
              });
              continue;
            }
            data.duplicates?.[k]?.forEach((ref, i) => {
              if (ref) this.excludedDuplicates[k].add(i);
            });
            this.entities[k].forEach((_, i) => {
              if (this.repeatOf(k, i) !== null) {
                this.excludedDuplicates[k].add(i);
              }
            });
          }
        }
        // What was just loaded IS the saved state — including a draft saved in
        // an earlier sitting. Without this the panel would open claiming
        // unsaved changes and immediately re-save an identical payload.
        this.version = data.version ?? 0;
        this.savedSignature = this.draftSignature;
        this.draftSavedAt = data.reviewed_at ?? null;
        // Only the first sitting: reopening a document already corrected (or
        // ingested) says nothing about what the model handed the reviewer.
        if (!this.draftSavedAt && !this.isIngested) {
          analytics.track("Extraction Review Opened", {
            report_id: data.report_uuid,
            report_confidence: data.extraction.confidence_score,
            entity_count: this.totalKept,
          });
        }
      }
    } catch (e: unknown) {
      // Reported before the staleness check: the panel showing this reason
      // instead of the global dialog is why it has to be reported at all, and
      // that is true whether or not a newer load has since won.
      maybeReportError(e);
      if (seq !== this.loadSeq) return;
      this.error = e;
    } finally {
      if (seq === this.loadSeq) {
        this.loading = false;
      }
    }
  }

  /**
   * Write the reviewed document to the patient's record, and return what the
   * server wrote.
   *
   * The entities do not travel: the server ingests the draft it holds, minus
   * what `excluded` marks. The caller has to have saved the draft first, or
   * the last corrections are ingested as they were before them.
   */
  async ingest(reportUuid: string): Promise<IngestResponse["ingested"]> {
    this.ingesting = true;
    try {
      const body: IngestPayload = { review_comment: this.reviewComment };
      const resp = await client.post<IngestResponse>({
        url: `/api/v1/medical-reports/${reportUuid}/extraction/ingest`,
        body,
      });
      analytics.track("Extraction Ingested", {
        report_id: reportUuid,
        report_confidence: this.result?.extraction.confidence_score ?? null,
        // What the server ingests: the draft minus what `excluded` marks.
        entity_count: this.totalKept,
      });
      return resp.data?.ingested ?? { skipped: 0 };
    } finally {
      this.ingesting = false;
    }
  }

  /** Mark the saved draft unusable; nothing is written to the patient. As with
   *  `ingest`, the caller saves the draft first. */
  async reject(reportUuid: string, reason: string): Promise<void> {
    this.rejecting = true;
    try {
      const body: RejectPayload = { rejected_reason: reason };
      await client.post({
        url: `/api/v1/medical-reports/${reportUuid}/extraction/reject`,
        body,
      });
    } finally {
      this.rejecting = false;
    }
  }

  async retry(reportUuid: string): Promise<void> {
    await client.post({
      url: `/api/v1/medical-reports/${reportUuid}/extraction/retry`,
      body: {},
    });
  }

  /**
   * File the report under `date` — the reviewer accepting the date the
   * document states over the one typed at upload. A partial update, so
   * nothing else about the report is touched.
   *
   * On the model rather than the patient store: the panel also opens from the
   * cross-patient queue, where no patient is loaded. Errors propagate, like
   * `ingest` — the server refuses some dates, in more than one shape, and the
   * panel is where that is turned into something a reviewer can read.
   */
  async applyReportDate(reportUuid: string, date: string): Promise<void> {
    this.applyingDate = true;
    try {
      await apiPutMedicalReportsUuid({
        path: { uuid: reportUuid },
        body: { report_date: date },
      });
    } finally {
      this.applyingDate = false;
    }
  }
}

/** The reviewed state as the extraction endpoint stores it. Exported so the
 *  tests assert against this shape rather than a structural cast of their own,
 *  which would keep passing after a field here was renamed. */
export interface DraftPayload {
  extraction: ExtractionResult;
  bboxes: ExtractionBBoxes;
  entity_confidence: ExtractionEntityConfidence;
  duplicates: ExtractionDuplicates;
  entity_origin: ExtractionEntityOrigin;
  excluded: ExtractionExcluded;
  entity_ids: ExtractionEntityIds;
  review_comment: string | null;
  version: number;
}

/** Body of the ingest endpoint — everything else it needs it already holds. */
interface IngestPayload {
  review_comment: string | null;
}

/** Response of the ingest endpoint, which the spec leaves untyped. */
interface IngestResponse {
  /** Rows written per kind, plus the entities the server could not write. */
  ingested: Record<string, number> & { skipped: number };
}

/** Body of the reject endpoint; the reason must not be blank. */
interface RejectPayload {
  rejected_reason: string;
}

/** One place, so the saved signature and the compared one cannot diverge.
 *
 *  `version` and `entity_ids` are left out — neither is a reviewer edit.
 *  `version` is bumped by every save, including someone else's, and an
 *  entity's id never changes as a correction, so including either would make
 *  `hasUnsavedEdits` report true with nothing to actually save.
 *  `JSON.stringify` drops `undefined`-valued keys, which is what excludes
 *  them here. */
function signatureOf(payload: DraftPayload): string {
  return JSON.stringify({
    ...payload,
    version: undefined,
    entity_ids: undefined,
  });
}
